import { useEffect, useId, useRef, useState } from "react";
import { api } from "../lib/api";
import { count, money } from "../lib/format";
import { CheckIcon, CloseIcon, DownloadIcon } from "../lib/ui";
import {
  isValidRange,
  longDate,
  periodRange,
  rangeLabel,
  shortSgt,
  todayInSingapore,
  type DateRange,
  type PeriodKind,
} from "../../shared/exportRange";
import type { RequestStatus } from "../lib/types";

interface Summary {
  total: number;
  paid: number;
  expired: number;
  waiting: number;
  received: number;
  tooMany: boolean;
  maxRows: number;
  preview: { reference: string; payer: string | null; amount: number | null; status: RequestStatus; requestedAt: string }[];
  firstDate: string | null;
  fileName: string;
}

type SummaryState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; data: Summary }
  | { kind: "error"; text: string };

type Phase = "idle" | "preparing" | "done" | "error";

const CHOICES: { kind: PeriodKind; label: string }[] = [
  { kind: "month", label: "This month" },
  { kind: "last", label: "Last month" },
  { kind: "quarter", label: "Last 3 months" },
  { kind: "year", label: "This year" },
  { kind: "all", label: "All time" },
  { kind: "custom", label: "Custom range" },
];

const COLUMNS = ["Reference", "Description", "Payer", "Payer email", "Amount", "Received", "Status", "Requested", "Paid"];
const STATUS_WORD: Record<RequestStatus, string> = { pending: "Waiting", confirmed: "Paid", expired: "Expired" };
const GENERIC_ERROR = "We couldn’t prepare the file. Nothing was downloaded. Try again in a moment.";

/**
 * The "Export CSV" bar above the request list and the panel it opens: pick a period, see what it
 * holds, download it. A period is decided in Singapore time (shared/exportRange.ts) by when each
 * request was created, and includes every status whatever the list is filtered to. Numbers and
 * first rows come from /api/export/summary, the file from /api/export/requests.
 */
export default function ExportBar({ total }: { total: number }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [today, setToday] = useState(() => todayInSingapore());
  const [period, setPeriod] = useState<PeriodKind>("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [summary, setSummary] = useState<SummaryState>({ kind: "idle" });
  const [firstDate, setFirstDate] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [errorText, setErrorText] = useState(GENERIC_ERROR);
  const [retry, setRetry] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const range: DateRange =
    period === "custom"
      ? { from: custom.from || null, to: custom.to || null }
      : periodRange(period, today);
  const customInvalid = period === "custom" && (!custom.from || !custom.to || !isValidRange(range));
  const dateProblem = period === "custom" && custom.from !== "" && custom.to !== "" && !isValidRange(range);

  const query = new URLSearchParams();
  if (range.from) query.set("from", range.from);
  if (range.to) query.set("to", range.to);
  const queryString = query.toString();

  // Count what the chosen period holds. Waits a beat while dates are being
  // typed, and ignores an answer that arrives after the choice has moved on.
  useEffect(() => {
    if (!open) return;
    setPhase("idle");
    if (customInvalid) {
      setSummary({ kind: "idle" });
      return;
    }
    setSummary({ kind: "loading" });
    let stale = false;
    const t = window.setTimeout(async () => {
      const res = await api.get<Summary>(`/api/export/summary?${queryString}`);
      if (stale) return;
      if (res.ok) {
        setSummary({ kind: "ready", data: res.data });
        if (res.data.firstDate) setFirstDate(res.data.firstDate);
      } else {
        setSummary({ kind: "error", text: res.error });
      }
    }, 250);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
    // queryString carries the whole range
  }, [open, queryString, customInvalid, retry]);

  // Opening moves focus to the chosen period, so the keyboard lands in it.
  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.focus();
  }, [open]);

  function openPanel() {
    setToday(todayInSingapore());
    setOpen(true);
  }

  function closePanel() {
    setOpen(false);
    setPhase("idle");
    window.setTimeout(() => btnRef.current?.focus(), 0);
  }

  function choose(kind: PeriodKind) {
    setPeriod(kind);
    if (kind === "custom" && !custom.from && !custom.to) {
      const last = periodRange("last", today);
      setCustom({ from: last.from ?? "", to: last.to ?? "" });
    }
  }

  async function download() {
    if (summary.kind !== "ready") return;
    const { fileName } = summary.data;
    setPhase("preparing");
    try {
      const res = await fetch(`/api/export/requests?${queryString}`, { credentials: "same-origin" });
      if (!res.ok) {
        let text = GENERIC_ERROR;
        if (res.status === 413 || res.status === 429) {
          try {
            text = ((await res.json()) as { error?: string }).error ?? text;
          } catch {
            /* keep the generic message */
          }
        }
        setErrorText(text);
        setPhase("error");
        return;
      }
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
      setPhase("done");
    } catch {
      setErrorText(GENERIC_ERROR);
      setPhase("error");
    }
  }

  const data = summary.kind === "ready" ? summary.data : null;
  const canDownload = data !== null && data.total > 0 && !data.tooMany && phase !== "preparing";
  const label = rangeLabel(
    period === "all" && firstDate ? { from: firstDate, to: today } : range,
  );

  return (
    <div className="ex">
      <div className="ex-bar">
        <div className="ex-title">
          <h2>All requests</h2>
          <span className="mono">{count(total)} in total</span>
        </div>
        <button
          type="button"
          ref={btnRef}
          className="ex-btn"
          aria-expanded={open}
          aria-controls={`${uid}-panel`}
          onClick={() => (open ? closePanel() : openPanel())}
        >
          <DownloadIcon size={18} />
          <span>Export CSV</span>
          <svg className="ex-chev" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 6l4 4 4-4" />
          </svg>
        </button>
      </div>

      {open && (
        <section
          id={`${uid}-panel`}
          ref={panelRef}
          className="ex-panel"
          aria-labelledby={`${uid}-h`}
          onKeyDown={(e) => {
            if (e.key === "Escape") closePanel();
          }}
        >
          <div className="ex-head">
            <div>
              <h3 id={`${uid}-h`}>Export requests to CSV</h3>
              <p>Pick a time period. The file opens in Excel, Numbers or Google Sheets.</p>
            </div>
            <button type="button" className="ex-close" aria-label="Close export" onClick={closePanel}>
              <CloseIcon size={18} />
            </button>
          </div>

          <div className="ex-grid">
            <div>
              <fieldset>
                <legend>Time period</legend>
                <div className="ex-choices">
                  {CHOICES.map((c) => {
                    const on = c.kind === period;
                    let sub = "Pick two dates";
                    if (c.kind === "all") sub = firstDate ? `Since ${longDate(firstDate)}` : "Since the first request";
                    else if (c.kind !== "custom") sub = rangeLabel(periodRange(c.kind, today));
                    return (
                      <label key={c.kind} className={"ex-choice" + (on ? " ex-choice--on" : "")}>
                        <input type="radio" name={`${uid}-period`} checked={on} onChange={() => choose(c.kind)} />
                        <span className="ct">{c.label}</span>
                        <span className="cd mono">{sub}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              {period === "custom" && (
                <>
                  <div className="ex-custom">
                    <div>
                      <label htmlFor={`${uid}-from`}>From</label>
                      <input id={`${uid}-from`} type="date" value={custom.from} aria-invalid={dateProblem || undefined} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
                    </div>
                    <div>
                      <label htmlFor={`${uid}-to`}>To</label>
                      <input id={`${uid}-to`} type="date" value={custom.to} aria-invalid={dateProblem || undefined} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
                    </div>
                  </div>
                  {dateProblem && <p className="ex-err" role="alert">The end date has to be on or after the start date.</p>}
                </>
              )}

              <p className="ex-hint">
                Periods use the date each request was created, in Singapore time. Every status is included, whatever the list below is filtered to.
              </p>
            </div>

            <div>
              {phase === "done" && data && (
                <div className="ex-note ex-note--ok" role="status">
                  <CheckIcon size={20} strokeWidth={2.4} />
                  <span>
                    <strong>Downloaded.</strong> <span className="mono">{data.fileName}</span> has {count(data.total)} row{data.total === 1 ? "" : "s"}. Check your Downloads folder.
                  </span>
                </div>
              )}
              {phase === "error" && (
                <div className="ex-note ex-note--err" role="alert">
                  <span>
                    <strong>{errorText === GENERIC_ERROR ? "We couldn’t prepare the file." : "Not downloaded."}</strong>{" "}
                    {errorText === GENERIC_ERROR ? "Nothing was downloaded. Try again in a moment." : errorText}
                  </span>
                </div>
              )}

              <div aria-live="polite">
                {customInvalid && (
                  <div className="ex-sum ex-sum--bad">
                    <div className="ex-k">In the file</div>
                    <div className="ex-s" style={{ marginTop: 6, fontWeight: 600 }}>
                      {dateProblem ? "Fix the dates to see what will be in the file." : "Pick both dates to see what will be in the file."}
                    </div>
                  </div>
                )}
                {!customInvalid && summary.kind === "loading" && (
                  <div className="ex-sum">
                    <div className="ex-k">In the file</div>
                    <div className="ex-s" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
                      <span className="spin" /> Counting…
                    </div>
                  </div>
                )}
                {!customInvalid && summary.kind === "error" && (
                  <div className="ex-sum ex-sum--bad">
                    <div className="ex-k">In the file</div>
                    <div className="ex-s" style={{ marginTop: 6, fontWeight: 600 }}>
                      We couldn’t count the requests. {summary.text}
                    </div>
                    <button type="button" className="ex-text" style={{ marginTop: 10 }} onClick={() => setRetry((n) => n + 1)}>
                      Try again
                    </button>
                  </div>
                )}
                {data && data.tooMany && (
                  <div className="ex-sum ex-sum--warn">
                    <div className="ex-k">In the file</div>
                    <div className="ex-v mono">Too many</div>
                    <div className="ex-s">
                      That is more than {data.maxRows.toLocaleString("en-US")} requests, the most one file can hold. Choose a shorter period.
                    </div>
                    <div className="ex-range mono">{label}</div>
                  </div>
                )}
                {data && !data.tooMany && data.total === 0 && (
                  <div className="ex-sum ex-sum--warn">
                    <div className="ex-k">In the file</div>
                    <div className="ex-v mono">No requests</div>
                    <div className="ex-s">Nothing was requested between these dates. Pick a longer period.</div>
                    <div className="ex-range mono">{label}</div>
                  </div>
                )}
                {data && !data.tooMany && data.total > 0 && (
                  <div className="ex-sum">
                    <div className="ex-k">In the file</div>
                    <div className="ex-v mono">{count(data.total)} request{data.total === 1 ? "" : "s"}</div>
                    <div className="ex-s">
                      {count(data.paid)} paid · {count(data.expired)} expired{data.waiting > 0 ? ` · ${count(data.waiting)} waiting` : ""}
                    </div>
                    <div className="ex-s">{money(data.received)} received</div>
                    <div className="ex-range mono">{label}</div>
                  </div>
                )}
              </div>

              {data && !data.tooMany && data.total > 0 && (
                <>
                  <div className="ex-block">
                    <div className="ex-k">Preview, newest first</div>
                    <div className="ex-prev" role="table" aria-label="Preview of the first rows in the file">
                      <div className="ex-row ex-row--h" role="row">
                        <span role="columnheader">Reference</span>
                        <span role="columnheader">Payer</span>
                        <span role="columnheader" className="num">Amount</span>
                        <span role="columnheader">Status</span>
                        <span role="columnheader">Requested</span>
                      </div>
                      {data.preview.map((r) => (
                        <div className="ex-row" role="row" key={r.reference}>
                          <span role="cell" className="mono">{r.reference}</span>
                          <span role="cell">{r.payer ?? "—"}</span>
                          <span role="cell" className="mono num">{r.amount != null ? r.amount.toFixed(2) : "—"}</span>
                          <span role="cell">{STATUS_WORD[r.status]}</span>
                          <span role="cell" className="mono">{shortSgt(r.requestedAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="ex-block">
                    <div className="ex-k">Columns</div>
                    <div className="ex-cols">
                      {COLUMNS.map((c) => (
                        <span className="ex-chip" key={c}>{c}</span>
                      ))}
                    </div>
                    <p className="ex-hint" style={{ marginTop: 12 }}>
                      Dates and times are in Singapore time. The file has payers’ names and emails in it. Keep it somewhere safe.
                    </p>
                  </div>
                  <div className="ex-file">
                    <span className="ex-k" style={{ flex: "none" }}>File</span>
                    <span className="mono">{data.fileName}</span>
                  </div>
                </>
              )}

              <div className="ex-actions">
                {phase === "done" ? (
                  <>
                    <button type="button" className="ex-ghost" onClick={() => setPhase("idle")}>Export another period</button>
                    <button type="button" className="ex-dl" onClick={closePanel}>Close</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="ex-dl" disabled={!canDownload} onClick={download}>
                      {phase === "preparing" ? <span className="spin" aria-hidden="true" /> : <DownloadIcon size={18} />}
                      <span aria-live="polite">
                        {phase === "preparing" ? `Preparing ${data ? count(data.total) : ""} rows…` : phase === "error" ? "Try again" : "Download CSV"}
                      </span>
                    </button>
                    <button type="button" className="ex-text" onClick={closePanel}>Cancel</button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
